/**
 * Prometheus Lab AI — API client for the single Aura FastAPI backend.
 *
 * Local:  http://localhost:8001  (same port as Aura OS)
 * Prod:   Railway / VITE_API_URL
 *
 * The legacy prometheus-lab-ai/backend on :8080 is deprecated.
 */

import { LOCAL_API_BASE, RAILWAY_API_BASE, resolveApiBase } from "@shared/config/apiUrls";

export { LOCAL_API_BASE, RAILWAY_API_BASE };

export const getBaseUrl = (): string => {
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1") {
      return LOCAL_API_BASE;
    }
  }
  return resolveApiBase(import.meta.env.VITE_API_URL as string | undefined);
};

export const API_BASE_URL = getBaseUrl();

/** Prefer Aura Supabase JWT; else workshop/anon key for single-tenant Pages. */
export function getAuthToken(): string {
  if (typeof window === "undefined") {
    return (import.meta.env.VITE_PROMETHEUS_WORKSHOP_TOKEN as string) || "workshop";
  }
  return (
    localStorage.getItem("sb-access-token") ||
    localStorage.getItem("prometheus_token") ||
    (import.meta.env.VITE_PROMETHEUS_WORKSHOP_TOKEN as string) ||
    "workshop"
  );
}

/** Fetch public anon key from API and cache as workshop bearer (no password needed). */
export async function bootstrapWorkshopAuth(): Promise<void> {
  if (typeof window === "undefined") return;
  const existing =
    localStorage.getItem("sb-access-token") || localStorage.getItem("prometheus_token");
  // Keep real user JWTs / prior anon cache; only bootstrap when missing or legacy placeholder
  if (existing && existing !== "workshop") return;
  try {
    const res = await fetch(`${getBaseUrl().replace(/\/$/, "")}/api/auth/config`);
    if (!res.ok) return;
    const cfg = await res.json();
    if (cfg?.anon_key) {
      localStorage.setItem("prometheus_token", cfg.anon_key);
    }
  } catch {
    // ignore — local/dev may use workshop bypass
  }
}

export function setAuthTokens(accessToken: string, refreshToken?: string): void {
  if (typeof window === "undefined") return;
  localStorage.setItem("sb-access-token", accessToken);
  localStorage.setItem("prometheus_token", accessToken);
  if (refreshToken) {
    localStorage.setItem("sb-refresh-token", refreshToken);
  }
}

export function clearAuthTokens(): void {
  if (typeof window === "undefined") return;
  localStorage.removeItem("sb-access-token");
  localStorage.removeItem("sb-refresh-token");
  localStorage.removeItem("prometheus_token");
}

/** Rewrite legacy :8080 / :8000 absolute URLs to the unified API base. */
export function rewriteLegacyApiUrl(url: string): string {
  const base = getBaseUrl().replace(/\/$/, "");
  return url
    .replace(/http:\/\/localhost:8080/g, base)
    .replace(/http:\/\/127\.0\.0\.1:8080/g, base)
    .replace(/http:\/\/localhost:8000/g, base)
    .replace(/http:\/\/127\.0\.0\.1:8000/g, base);
}

export interface HealthStatus {
  status: string;
  service: string;
  version: string;
  has_google_key: boolean;
  has_openai_key: boolean;
  has_anthropic_key: boolean;
}

export interface ChatPayload {
  characterId: string;
  message: string;
  modelId: string;
  image?: string;
  googleKey?: string;
  openaiKey?: string;
  anthropicKey?: string;
}

export interface ChatResponse {
  response: string;
  modelUsed: string;
  toolCalls?: Array<{
    name: string;
    args: Record<string, any>;
  }>;
}

export async function fetchApi<T = any>(
  endpoint: string,
  options: RequestInit = {},
  timeoutMs: number = 8000
): Promise<T> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  const cleanEndpoint = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
  const url = `${getBaseUrl().replace(/\/$/, "")}${cleanEndpoint}`;

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getAuthToken()}`,
        ...(options.headers || {}),
      },
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorBody = await response.text().catch(() => "");
      throw new Error(`API Error ${response.status}: ${errorBody || response.statusText}`);
    }

    return (await response.json()) as T;
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === "AbortError") {
      throw new Error(`API Timeout after ${timeoutMs}ms calling ${cleanEndpoint}`);
    }
    throw error;
  }
}

export const prometheusApi = {
  getHealth: async (): Promise<HealthStatus> => {
    return fetchApi<HealthStatus>("/api/health", { method: "GET" }, 5000);
  },

  login: async (email: string, password: string): Promise<{ access_token: string; refresh_token?: string; user?: { id?: string; email?: string } }> => {
    const data = await fetchApi<{
      access_token: string;
      refresh_token?: string;
      user?: { id?: string; email?: string };
    }>(
      "/api/auth/login",
      {
        method: "POST",
        body: JSON.stringify({ email, password }),
        headers: {}, // login itself should not require prior Bearer
      },
      15000
    );
    if (data.access_token) {
      setAuthTokens(data.access_token, data.refresh_token);
    }
    return data;
  },

  me: async (): Promise<{ id: string; email?: string; username?: string }> => {
    return fetchApi("/api/auth/me", { method: "GET" }, 8000);
  },

  logout: (): void => {
    clearAuthTokens();
  },

  sendChat: async (payload: ChatPayload): Promise<ChatResponse> => {
    return fetchApi<ChatResponse>(
      "/api/chat",
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
      25000
    );
  },

  getProjects: async (): Promise<any[]> => {
    return fetchApi<any[]>("/api/projects", { method: "GET" });
  },

  generateProject: async (params: Record<string, any>): Promise<any> => {
    return fetchApi<any>(
      "/api/projects/generate",
      {
        method: "POST",
        body: JSON.stringify(params),
      },
      30000
    );
  },

  brainstormProjects: async (disciplines: string[]): Promise<any> => {
    return fetchApi<any>(
      "/api/projects/brainstorm",
      {
        method: "POST",
        body: JSON.stringify({ disciplines }),
      },
      25000
    );
  },

  getLogs: async (): Promise<any[]> => {
    return fetchApi<any[]>("/api/logs", { method: "GET" });
  },

  createLog: async (logData: Record<string, any>): Promise<any> => {
    return fetchApi<any>("/api/logs", {
      method: "POST",
      body: JSON.stringify(logData),
    });
  },

  getInventory: async (): Promise<any[]> => {
    return fetchApi<any[]>("/api/inventory", { method: "GET" });
  },

  createInventoryItem: async (item: Record<string, any>): Promise<any> => {
    return fetchApi<any>("/api/inventory", {
      method: "POST",
      body: JSON.stringify(item),
    });
  },

  updateInventoryItem: async (itemId: string, updates: Record<string, any>): Promise<any> => {
    return fetchApi<any>(`/api/inventory/${itemId}`, {
      method: "PATCH",
      body: JSON.stringify(updates),
    });
  },

  deleteInventoryItem: async (itemId: string): Promise<any> => {
    return fetchApi<any>(`/api/inventory/${itemId}`, {
      method: "DELETE",
    });
  },

  scanVisualItem: async (imageB64: string, prompt?: string): Promise<any> => {
    return fetchApi<any>(
      "/api/vision/scan",
      {
        method: "POST",
        body: JSON.stringify({ image: imageB64, prompt }),
      },
      20000
    );
  },

  /** SDD 003 Workshop Copilot — classify NL (no mutation). */
  workshopIntent: async (message: string): Promise<{
    success: boolean;
    brand: string;
    intent: string;
    confidence: number;
    clarification?: string | null;
    preview?: { summary?: string; requires_confirmation?: boolean; action?: string; payload?: Record<string, unknown> };
    confirmation_id?: string | null;
    catalog?: {
      query?: string;
      empty?: boolean;
      suggestion?: string | null;
      groups?: Array<{
        id: string;
        label: string;
        hint?: string;
        count: number;
        items: Array<Record<string, any>>;
      }>;
      total?: number;
      standards_note?: string;
      brand?: string;
    } | null;
    qa?: {
      question?: string;
      answer?: string;
      citations?: Array<{ label: string; kind?: string; detail?: string }>;
      sourced?: boolean;
      estimate_without_source?: boolean;
      label?: string | null;
      disclaimer?: string | null;
      brand?: string;
      suggestion?: string;
    } | null;
    artifact?: {
      ready?: boolean;
      label?: string | null;
      message?: string;
      missing_dimensions?: string[];
      dimensions?: Record<string, number>;
      script?: string | null;
      bom?: Array<{ name: string; qty: number; note?: string }>;
      filename_suggestion?: string;
      brand?: string;
    } | null;
    persisted?: boolean;
    note?: string | null;
  }> => {
    return fetchApi("/api/prometheus/workshop/intent", {
      method: "POST",
      body: JSON.stringify({ message }),
    }, 12000);
  },

  /** SDD 003 Workshop Copilot — confirm mutation. */
  workshopConfirm: async (confirmationId: string): Promise<{
    success: boolean;
    executed?: boolean;
    duplicate?: boolean;
    brand?: string;
    summary?: string;
    message?: string;
    intent?: string;
  }> => {
    return fetchApi("/api/prometheus/workshop/confirm", {
      method: "POST",
      body: JSON.stringify({ confirmation_id: confirmationId }),
    }, 20000);
  },

  workshopCatalog: async (query: string): Promise<{
    success: boolean;
    brand?: string;
    query: string;
    empty: boolean;
    suggestion?: string | null;
    groups: Array<{
      id: string;
      label: string;
      hint?: string;
      count: number;
      items: Array<Record<string, any>>;
    }>;
    total?: number;
    standards_note?: string;
  }> => {
    return fetchApi("/api/prometheus/workshop/catalog", {
      method: "POST",
      body: JSON.stringify({ query }),
    }, 12000);
  },

  workshopQa: async (question: string): Promise<{
    success: boolean;
    brand?: string;
    question: string;
    answer: string;
    citations: Array<{ label: string; kind?: string; detail?: string }>;
    sourced: boolean;
    estimate_without_source?: boolean;
    label?: string | null;
    disclaimer?: string | null;
    suggestion?: string;
  }> => {
    return fetchApi("/api/prometheus/workshop/qa", {
      method: "POST",
      body: JSON.stringify({ question }),
    }, 12000);
  },

  workshopArtifact: async (description: string): Promise<{
    success: boolean;
    ready: boolean;
    label?: string | null;
    message?: string;
    missing_dimensions?: string[];
    script?: string | null;
    bom?: Array<{ name: string; qty: number; note?: string }>;
    filename_suggestion?: string;
  }> => {
    return fetchApi("/api/prometheus/workshop/artifact", {
      method: "POST",
      body: JSON.stringify({ description }),
    }, 12000);
  },
};
